import { ref, set, get, update, remove, onValue, off, push } from 'firebase/database';
import { db } from '../lib/firebase';
import { updateUserScores } from './leaderboardService';
import { logUserActivity } from './activityLogService';
import { compareTextsV2 } from '../utils/textComparison';
import {
  SessionGameType,
  SessionGameContentSource,
  SessionGameState,
  SessionGameRound,
  SessionPlayerState,
  WordComparisonItem,
  GameOptionInfo
} from '../types/session-games';
import { SessionStudyMaterial, SessionSavedItem } from './friendSessionService';

// Sanitize helper for RTDB (removes undefined fields)
const sanitizeForFirebase = <T extends Record<string, any>>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj, (_, v) => (v === undefined ? null : v)));
};

export const GAME_OPTIONS: GameOptionInfo[] = [
  {
    type: 'pronunciation_duel',
    title: 'Pronunciation Duel',
    description: 'Listen, speak into your mic, and compare pronunciation accuracy with real-time phonetic feedback!',
    skills: ['speaking', 'pronunciation', 'listening'],
    estimatedDuration: '2-3 min',
    iconName: 'Mic',
    badgeColor: 'from-emerald-500 to-teal-600',
    supportsContentSources: ['news', 'saved_words', 'thematic']
  },
  {
    type: 'sentence_architect',
    title: 'Sentence Architect',
    description: 'Reconstruct scrambled news sentences and phrases. Test grammar, word order, and syntax.',
    skills: ['sentences', 'reading', 'vocabulary'],
    estimatedDuration: '3 min',
    iconName: 'Boxes',
    badgeColor: 'from-blue-500 to-indigo-600',
    supportsContentSources: ['news', 'saved_words', 'thematic']
  },
  {
    type: 'translation_duel',
    title: 'Flash Translation Duel',
    description: 'Rapid-fire translation buzzer! First to pick the correct translation locks the highest score.',
    skills: ['translation', 'vocabulary'],
    estimatedDuration: '2 min',
    iconName: 'Zap',
    badgeColor: 'from-amber-500 to-orange-600',
    supportsContentSources: ['saved_words', 'news', 'thematic']
  },
  {
    type: 'news_detective',
    title: 'News Detective',
    description: 'Uncover missing key vocabulary in real news stories using audio, definition, and contextual clues.',
    skills: ['reading', 'vocabulary', 'listening'],
    estimatedDuration: '3-4 min',
    iconName: 'Search',
    badgeColor: 'from-purple-500 to-violet-600',
    supportsContentSources: ['news', 'thematic']
  },
  {
    type: 'audio_mystery',
    title: 'Audio Mystery',
    description: 'Masked listening comprehension! Listen to authentic speech and reconstruct the sentence or repeat what you heard.',
    skills: ['listening', 'speaking', 'sentences'],
    estimatedDuration: '2-3 min',
    iconName: 'Headphones',
    badgeColor: 'from-pink-500 to-rose-600',
    supportsContentSources: ['news', 'saved_words', 'thematic']
  },
  {
    type: 'word_tennis',
    title: 'Word Tennis',
    description: '15-second shot clock vocabulary rally! Chained words validated against the live English dictionary.',
    skills: ['vocabulary', 'speaking'],
    estimatedDuration: '2 min',
    iconName: 'Activity',
    badgeColor: 'from-cyan-500 to-blue-600',
    supportsContentSources: ['news', 'thematic']
  },
  {
    type: 'quick_stop',
    title: 'Quick STOP Battle',
    description: 'Classic 2-player STOP game inside the room with live categories and instant letter wheel.',
    skills: ['vocabulary', 'reading'],
    estimatedDuration: '3 min',
    iconName: 'StopCircle',
    badgeColor: 'from-red-500 to-rose-700',
    supportsContentSources: ['thematic']
  }
];

// Fallback high-quality curated rounds across conversational and news topics
const CURATED_FALLBACK_ROUNDS = [
  {
    targetText: 'Artificial intelligence is reshaping the future of modern communication.',
    translation: 'A inteligência artificial está remodelando o futuro da comunicação moderna.',
    category: 'Technology',
    targetWord: 'communication',
    definition: 'The imparting or exchanging of information by speaking, writing, or using another medium.'
  },
  {
    targetText: 'Consistent daily practice is the true key to mastering a foreign language.',
    translation: 'A prática diária consistente é a verdadeira chave para dominar um idioma estrangeiro.',
    category: 'Education',
    targetWord: 'mastering',
    definition: 'Acquiring complete knowledge or skill in an accomplishment or technique.'
  },
  {
    targetText: 'Sustainable energy sources help reduce carbon emissions across the globe.',
    translation: 'Fontes de energia sustentáveis ajudam a reduzir as emissões de carbono pelo mundo.',
    category: 'Environment',
    targetWord: 'sustainable',
    definition: 'Able to be maintained at a certain rate or level without depleting natural resources.'
  },
  {
    targetText: 'Traveling to new destinations expands our perspective and cultural appreciation.',
    translation: 'Viajar para novos destinos expande nossa perspectiva e apreciação cultural.',
    category: 'Travel',
    targetWord: 'perspective',
    definition: 'A particular attitude toward or way of regarding something; a point of view.'
  },
  {
    targetText: 'Healthy habits combined with good sleep improve mental focus and creativity.',
    translation: 'Hábitos saudáveis combinados com um bom sono melhoram o foco mental e a criatividade.',
    category: 'Health',
    targetWord: 'creativity',
    definition: 'The use of the imagination or original ideas, especially in the production of artistic work.'
  }
];

const TRANSLATION_DISTRACTORS: Record<string, string[]> = {
  general: ['desenvolvimento', 'oportunidade', 'conhecimento', 'experiência', 'comunicação', 'progresso', 'investigação', 'estratégia', 'resultado', 'transformação']
};

/**
 * Clean sentence string
 */
function cleanSentence(sentence: string): string {
  return sentence.replace(/\s+/g, ' ').trim();
}

/**
 * Shuffles array deterministically / randomly
 */
function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Extracts valid sentences from material text
 */
function extractSentencesFromMaterial(content?: string): string[] {
  if (!content) return [];
  // Split by periods, exclamation marks, question marks, or double newlines
  const rawSentences = content
    .replace(/\n+/g, ' ')
    .split(/(?<=[.?!])\s+/)
    .map(s => cleanSentence(s))
    .filter(s => {
      const words = s.split(/\s+/).filter(Boolean);
      // Valid sentences have between 4 and 20 words and end with punctuation
      return words.length >= 4 && words.length <= 25 && /[a-zA-Z]/.test(s);
    });

  return rawSentences;
}

/**
 * Generates game rounds based on game type, source, material, and saved items
 */
export function generateGameRounds(
  gameType: SessionGameType,
  source: SessionGameContentSource,
  material: SessionStudyMaterial | null,
  savedItems: SessionSavedItem[] = [],
  desiredCount = 5
): SessionGameRound[] {
  const rounds: SessionGameRound[] = [];
  const materialSentences = source === 'news' && material?.content
    ? extractSentencesFromMaterial(material.content)
    : [];

  const eligibleSavedWords = savedItems.filter(item => item.word && item.word.trim().length > 0);

  // Helper to get fallback pool
  const getFallbackItem = (idx: number) => {
    return CURATED_FALLBACK_ROUNDS[idx % CURATED_FALLBACK_ROUNDS.length];
  };

  for (let i = 0; i < desiredCount; i++) {
    const roundId = `round_${i + 1}`;

    if (gameType === 'pronunciation_duel') {
      let sentence = '';
      let translation = '';

      if (source === 'news' && materialSentences[i]) {
        sentence = materialSentences[i];
      } else if (source === 'saved_words' && eligibleSavedWords[i]) {
        const item = eligibleSavedWords[i];
        sentence = item.exampleSentence || `Let us practice the word ${item.word} today.`;
        translation = item.exampleTranslation || item.translation || '';
      } else {
        const fallback = getFallbackItem(i);
        sentence = fallback.targetText;
        translation = fallback.translation;
      }

      rounds.push({
        id: roundId,
        prompt: 'Speak this sentence into your microphone clearly:',
        targetText: sentence,
        translation,
        correctAnswer: sentence
      });
    } else if (gameType === 'sentence_architect') {
      let sentence = '';
      let translation = '';

      if (source === 'news' && materialSentences[i]) {
        sentence = materialSentences[i];
      } else if (source === 'saved_words' && eligibleSavedWords[i]) {
        const item = eligibleSavedWords[i];
        sentence = item.exampleSentence || `We can learn ${item.word} together.`;
        translation = item.exampleTranslation || item.translation || '';
      } else {
        const fallback = getFallbackItem(i);
        sentence = fallback.targetText;
        translation = fallback.translation;
      }

      const words = sentence.split(/\s+/).filter(Boolean);
      const scrambled = shuffleArray(words);
      // Ensure scrambled is different from original if length > 2
      if (scrambled.join(' ') === words.join(' ') && words.length > 2) {
        scrambled.reverse();
      }

      rounds.push({
        id: roundId,
        prompt: 'Arrange the words in correct grammatical order:',
        targetText: sentence,
        translation,
        scrambledWords: scrambled,
        correctAnswer: words.join(' ')
      });
    } else if (gameType === 'translation_duel') {
      let wordOrPhrase = '';
      let correctTranslation = '';

      if (source === 'saved_words' && eligibleSavedWords[i]?.translation) {
        wordOrPhrase = eligibleSavedWords[i].word;
        correctTranslation = eligibleSavedWords[i].translation!;
      } else if (source === 'news' && eligibleSavedWords[i]?.translation) {
        wordOrPhrase = eligibleSavedWords[i].word;
        correctTranslation = eligibleSavedWords[i].translation!;
      } else {
        const fallback = getFallbackItem(i);
        wordOrPhrase = fallback.targetWord;
        correctTranslation = fallback.translation.split(' ')[0] || 'significado';
      }

      // Generate 3 distractors
      const distractors = TRANSLATION_DISTRACTORS.general.filter(d => d.toLowerCase() !== correctTranslation.toLowerCase());
      const shuffledDistractors = shuffleArray(distractors).slice(0, 3);
      const options = shuffleArray([correctTranslation, ...shuffledDistractors]);

      rounds.push({
        id: roundId,
        prompt: `Translate to Portuguese: "${wordOrPhrase}"`,
        targetText: wordOrPhrase,
        options,
        correctAnswer: correctTranslation
      });
    } else if (gameType === 'news_detective') {
      let sentence = '';
      let targetWord = '';
      let definition = '';

      if (source === 'news' && materialSentences[i]) {
        sentence = materialSentences[i];
        const words = sentence.split(/\s+/).filter(w => w.replace(/[^a-zA-Z]/g, '').length >= 5);
        targetWord = words[Math.floor(Math.random() * words.length)] || words[0] || 'world';
        targetWord = targetWord.replace(/[.,!?;:]/g, '');
        definition = 'Important keyword appearing in the news context.';
      } else {
        const fallback = getFallbackItem(i);
        sentence = fallback.targetText;
        targetWord = fallback.targetWord;
        definition = fallback.definition;
      }

      // Replace target word in sentence with [ ? ]
      const regex = new RegExp(`\\b${targetWord}\\b`, 'i');
      const clozePrompt = sentence.replace(regex, '[ ? ]');

      const distractors = ['development', 'system', 'process', 'solution', 'concept'].filter(d => d !== targetWord.toLowerCase());
      const options = shuffleArray([targetWord, ...distractors.slice(0, 3)]);

      rounds.push({
        id: roundId,
        prompt: `Find the missing word: "${clozePrompt}"`,
        targetText: sentence,
        options,
        correctAnswer: targetWord,
        clues: {
          definition,
          translationClue: `Related to: ${targetWord.slice(0, 2)}...`,
          audioClue: targetWord
        }
      });
    } else if (gameType === 'audio_mystery') {
      let sentence = '';
      let translation = '';

      if (source === 'news' && materialSentences[i]) {
        sentence = materialSentences[i];
      } else {
        const fallback = getFallbackItem(i);
        sentence = fallback.targetText;
        translation = fallback.translation;
      }

      const words = sentence.split(/\s+/).filter(Boolean);
      rounds.push({
        id: roundId,
        prompt: 'Listen to the audio and reconstruct what you hear:',
        targetText: sentence,
        translation,
        scrambledWords: shuffleArray(words),
        correctAnswer: sentence
      });
    } else if (gameType === 'word_tennis') {
      const topics = ['Technology', 'Travel & Places', 'Health & Body', 'Food & Cuisine', 'Daily Routine'];
      const topic = topics[i % topics.length];

      rounds.push({
        id: roundId,
        prompt: `Word Tennis topic: ${topic}`,
        targetText: topic,
        correctAnswer: topic
      });
    } else {
      // quick_stop
      rounds.push({
        id: roundId,
        prompt: 'Quick STOP round',
        targetText: 'STOP',
        correctAnswer: 'STOP'
      });
    }
  }

  return rounds;
}

/**
 * Evaluates spoken audio transcript against original sentence
 * using compareTextsV2 (phonetic + Levenshtein).
 */
export function calculatePronunciationScore(
  targetText: string,
  spokenText: string
): { accuracyScore: number; diff: WordComparisonItem[] } {
  if (!spokenText || !spokenText.trim()) {
    const cleanOriginalWords = targetText.replace(/[.,!?;:]/g, '').split(/\s+/).filter(Boolean);
    return {
      accuracyScore: 0,
      diff: cleanOriginalWords.map(w => ({
        original: w,
        spoken: '',
        isMatch: false,
        similarity: 0,
        status: 'incorrect'
      }))
    };
  }

  const comparison = compareTextsV2(targetText, spokenText);
  const diff: WordComparisonItem[] = comparison.map(c => ({
    original: c.original,
    spoken: c.spoken,
    isMatch: c.isMatch,
    similarity: c.similarity,
    status: c.status
  }));

  const total = diff.length || 1;
  const correctCount = diff.filter(d => d.isMatch).length;
  const closeCount = diff.filter(d => d.status === 'close').length;

  const rawScore = (correctCount * 1.0 + closeCount * 0.75) / total;
  const accuracyScore = Math.min(100, Math.max(0, Math.round(rawScore * 100)));

  return { accuracyScore, diff };
}

/**
 * Validates a sentence arrangement
 */
export function validateSentenceAnswer(correctSentence: string, candidateWords: string[]): boolean {
  const clean = (s: string) =>
    s
      .toLowerCase()
      .replace(/[.,!?;:]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const userSentence = clean(candidateWords.join(' '));
  const expected = clean(correctSentence);

  return userSentence === expected;
}

/**
 * Starts a new learning game inside a friend session room
 */
export async function startSessionGame(
  sessionId: string,
  gameType: SessionGameType,
  contentSource: SessionGameContentSource,
  material: SessionStudyMaterial | null,
  savedItems: SessionSavedItem[] = [],
  host: { id: string; name: string; photo?: string },
  friend: { id: string; name: string; photo?: string }
): Promise<SessionGameState> {
  const rounds = generateGameRounds(gameType, contentSource, material, savedItems, 4);
  const now = Date.now();
  const gameRef = ref(db, `friendSessions/${sessionId}/activeGame`);

  const initialPlayers: Record<string, SessionPlayerState> = {
    [host.id]: {
      userId: host.id,
      userName: host.name,
      userPhoto: host.photo || '',
      score: 0,
      isReady: true,
      roundFinished: false,
      streakCount: 0
    },
    [friend.id]: {
      userId: friend.id,
      userName: friend.name,
      userPhoto: friend.photo || '',
      score: 0,
      isReady: true,
      roundFinished: false,
      streakCount: 0
    }
  };

  const newGame: SessionGameState = {
    gameId: `game_${now}_${Math.random().toString(36).substring(2, 7)}`,
    type: gameType,
    status: 'playing',
    contentSource,
    currentRoundIndex: 0,
    totalRounds: rounds.length,
    roundDurationSeconds: gameType === 'translation_duel' ? 15 : gameType === 'word_tennis' ? 15 : 45,
    roundStartTime: now,
    roundEndTime: now + 45000,
    turnUserId: host.id,
    rounds,
    players: initialPlayers,
    updatedAt: now,
    createdBy: host.id
  };

  await set(gameRef, sanitizeForFirebase(newGame));
  return newGame;
}

/**
 * Submits player action or answer for the current round
 */
export async function submitPlayerAnswer(
  sessionId: string,
  userId: string,
  submission: {
    answer?: any;
    isCorrect?: boolean;
    points?: number;
    accuracyScore?: number;
    wordDiff?: WordComparisonItem[];
    micTranscript?: string;
  }
): Promise<void> {
  const gameRef = ref(db, `friendSessions/${sessionId}/activeGame`);
  const snap = await get(gameRef);
  if (!snap.exists()) return;

  const game = snap.val() as SessionGameState;
  const player = game.players[userId] || {
    userId,
    userName: 'Player',
    score: 0,
    isReady: true,
    roundFinished: false
  };

  const earnedPoints = submission.points || (submission.isCorrect ? 10 : 0);
  const newScore = (player.score || 0) + earnedPoints;
  const newStreak = submission.isCorrect ? (player.streakCount || 0) + 1 : 0;

  const updates: Record<string, any> = {
    [`players/${userId}/roundFinished`]: true,
    [`players/${userId}/score`]: newScore,
    [`players/${userId}/streakCount`]: newStreak,
    [`players/${userId}/currentAnswer`]: submission.answer ?? '',
    [`players/${userId}/accuracyScore`]: submission.accuracyScore ?? null,
    [`players/${userId}/wordDiff`]: submission.wordDiff ?? null,
    [`players/${userId}/micTranscript`]: submission.micTranscript ?? null,
    updatedAt: Date.now()
  };

  // Check if other player is also finished
  const otherPlayerKey = Object.keys(game.players).find(k => k !== userId);
  const otherPlayerFinished = otherPlayerKey ? game.players[otherPlayerKey]?.roundFinished : true;

  if (otherPlayerFinished) {
    updates['status'] = 'round_result';
  }

  await update(gameRef, sanitizeForFirebase(updates));
}

/**
 * Advances to the next round, or transitions to game_over if on the last round
 */
export async function advanceSessionRound(sessionId: string): Promise<void> {
  const gameRef = ref(db, `friendSessions/${sessionId}/activeGame`);
  const snap = await get(gameRef);
  if (!snap.exists()) return;

  const game = snap.val() as SessionGameState;
  const now = Date.now();
  const nextRoundIndex = game.currentRoundIndex + 1;

  if (nextRoundIndex < game.totalRounds) {
    // Move to next round
    const playerKeys = Object.keys(game.players);
    const updates: Record<string, any> = {
      status: 'playing',
      currentRoundIndex: nextRoundIndex,
      roundStartTime: now,
      roundEndTime: now + (game.roundDurationSeconds || 45) * 1000,
      updatedAt: now
    };

    playerKeys.forEach(pk => {
      updates[`players/${pk}/roundFinished`] = false;
      updates[`players/${pk}/currentAnswer`] = null;
      updates[`players/${pk}/accuracyScore`] = null;
      updates[`players/${pk}/wordDiff`] = null;
      updates[`players/${pk}/micTranscript`] = null;
    });

    await update(gameRef, sanitizeForFirebase(updates));
  } else {
    // Game Over: determine winner
    const playersArr = Object.values(game.players);
    let winnerId: string | 'tie' = 'tie';
    if (playersArr.length === 2) {
      if (playersArr[0].score > playersArr[1].score) {
        winnerId = playersArr[0].userId;
      } else if (playersArr[1].score > playersArr[0].score) {
        winnerId = playersArr[1].userId;
      }
    }

    const updates: Record<string, any> = {
      status: 'game_over',
      winnerId,
      updatedAt: now
    };

    await update(gameRef, sanitizeForFirebase(updates));

    // Award activity points and logs per project memory rule (grouped at end of game)
    for (const p of playersArr) {
      const award = Math.max(30, p.score * 2);
      try {
        await updateUserScores(p.userId, award);
        await logUserActivity({
          userId: p.userId,
          type: 'training_completed',
          points: award,
          details: `Completed Friend Session Game: ${game.type} (Score: ${p.score})`
        });
      } catch (err) {
        console.error(`Error recording game scores for user ${p.userId}:`, err);
      }
    }
  }
}

/**
 * Closes or cancels the active session game
 */
export async function cancelOrEndSessionGame(sessionId: string): Promise<void> {
  const gameRef = ref(db, `friendSessions/${sessionId}/activeGame`);
  await remove(gameRef);
}

/**
 * Real-time listener for the active game in the friend session
 */
export function listenToActiveGame(
  sessionId: string,
  callback: (game: SessionGameState | null) => void
): () => void {
  const gameRef = ref(db, `friendSessions/${sessionId}/activeGame`);
  const unsubscribe = onValue(gameRef, snap => {
    if (snap.exists()) {
      callback(snap.val() as SessionGameState);
    } else {
      callback(null);
    }
  });

  return () => {
    off(gameRef);
  };
}
